import {
  FetchUserAttributesOutput,
  SignUpOutput,
  UserAttributeKey,
  autoSignIn,
  confirmResetPassword,
  confirmSignUp,
  deleteWebAuthnCredential,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updateUserAttributes as AuthUpdateUserAttributes,
  listWebAuthnCredentials,
  associateWebAuthnCredential,
} from "@aws-amplify/auth";
import { AuthProvider } from "@aws-amplify/auth/dist/esm/types/inputs";
import {
  CodeMismatchError,
  ExpiredCodeError,
  LimitExceededError,
  NotAuthorizedError,
  PasswordPolicyError,
  UsernameExistsError,
  UsernameIsEmailError,
} from "./authErrors";
import { CognitoUser, CreatorType, SignUpProperties } from "./models";
import { AuthNextSignInStep } from "@aws-amplify/auth/dist/esm/types";

interface CognitoUserAttributes {
  name: string;
  family_name: string;
}
class AuthClientClass {
  public async getToken(): Promise<string> {
    const session = await fetchAuthSession();
    return session.tokens.idToken.toString();
  }

  public async getUser(): Promise<CognitoUser> {
    const data = await fetchUserAttributes();
    return this.userInfoToUser(data);
  }

  async signUp(properties: SignUpProperties): Promise<SignUpOutput> {
    try {
      return await signUp({
        username: properties.username,
        password: properties.password,
        options: {
          userAttributes: {
            ...properties.attributes,
          },
          autoSignIn: true,
        },
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async confirmSignUp(email: string, confirmationCode: string): Promise<any> {
    try {
      const { nextStep: confirmSignUpNextStep } = await confirmSignUp({
        username: email,
        confirmationCode,
      });

      if (confirmSignUpNextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        // Call `autoSignIn` API to complete the flow
        const { nextStep } = await autoSignIn();

        if (nextStep.signInStep === "DONE") {
          return;
        }
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  async signInBasic(email: string, password: string): Promise<any> {
    try {
      return await signIn({ username: email, password });
    } catch (error) {
      this.handleError(error);
    }
  }

  async signInWithPasskey(
    username: string,
  ): Promise<AuthNextSignInStep<UserAttributeKey> | null> {
    try {
      const s = await signIn({
        username,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "WEB_AUTHN",
        },
      });
      if (s.isSignedIn || s.nextStep.signInStep === "DONE") {
        return;
      }
      return s.nextStep;
    } catch (error) {
      this.handleError(error);
    }
  }

  async associateWebAuthnCredential() {
    return await associateWebAuthnCredential();
  }

  async listWebAuthnCredentials(nextToken?: string) {
    return await listWebAuthnCredentials({
      nextToken,
    });
  }

  async deleteWebAuthnCredential(credentialId: string) {
    return await deleteWebAuthnCredential({
      credentialId,
    });
  }

  async signInFederated(identity_provider: AuthProvider): Promise<void> {
    try {
      return await signInWithRedirect({
        provider: identity_provider,
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  async signOut({ global = false }: { global?: boolean }): Promise<void> {
    await signOut({ global });
  }

  async resendEmailVerification(username: string): Promise<any> {
    return await resendSignUpCode({ username });
  }

  // Return is AWSCredential, but is not exported by the sdk
  async userCredentials(): Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
    expiration?: Date;
  }> {
    return (await fetchAuthSession())?.credentials;
  }

  async forgotPassword(username: string): Promise<any> {
    return await resetPassword({ username });
  }

  async forgotPasswordSubmit(
    username: string,
    code: string,
    password: string,
  ): Promise<void> {
    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword: password,
    });
    return await this.signInBasic(username, password);
  }

  private userInfoToUser(attributes: FetchUserAttributesOutput): CognitoUser {
    const firstName = attributes.name?.toLowerCase() ?? "";
    const lastName = attributes.family_name?.toLowerCase() ?? "";
    const user: CognitoUser = {
      type: CreatorType.tm,
      username: attributes["preferred_username"],
      email: attributes["email"],
      id: attributes["custom:id"] ?? attributes["sub"],
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
    };

    if (attributes.identities != null) {
      const identities = JSON.parse(attributes.identities);
      user.provider = identities[0].providerName ?? null;
    }
    return user;
  }

  async updateUserAttributes(
    userAttributes: Partial<CognitoUserAttributes>,
  ): Promise<void> {
    try {
      await AuthUpdateUserAttributes({ userAttributes });
    } catch (error) {
      this.handleError(error);
    }
  }

  private handleError(error: unknown) {
    const exceptionName = (error as Error).name;
    const exceptionMessage = (error as Error).message;

    switch (exceptionName) {
      case "InvalidPasswordException":
        throw new PasswordPolicyError();
      case "UsernameExistsException":
        throw new UsernameExistsError();
      case "ExpiredCodeException":
        throw new ExpiredCodeError();
      case "CodeMismatchException":
        throw new CodeMismatchError();
      case "LimitExceededException":
        throw new LimitExceededError();
      case "UserUnAuthenticatedException":
        throw new NotAuthorizedError(exceptionMessage);
      case "NotAuthorizedException":
        throw new NotAuthorizedError(exceptionMessage);
      case "InvalidParameterException":
        if (
          exceptionMessage ===
          "Username cannot be of email format, since user pool is configured for email alias."
        ) {
          throw new UsernameIsEmailError();
        }
      default:
        throw error;
    }
  }
}

export const AuthClient = new AuthClientClass();
